import { TrendingDownRounded, TrendingFlatRounded, TrendingUpRounded } from '@mui/icons-material';
import { Box, Typography } from '@mui/material';
import { COLORS } from '../theme';

interface IProps {
  value: number; // percent, e.g. 12 or -3
  suffix?: string; // e.g. 'vs prior period'
  /**
   * Which direction is *good* for this metric. Defaults to 'up', which is wrong
   * for cost metrics — a rising CPM or frequency was previously painted green.
   * 'neutral' greys the delta for metrics with no inherent polarity (spend).
   */
  goodDirection?: 'up' | 'down' | 'neutral';
}

/** Green/red/neutral trend delta with an arrow, e.g. "↗ +12.0% vs prior period". */
const Delta = ({ value, suffix, goodDirection = 'up' }: IProps) => {
  // Guard non-finite input: a non-numeric metric produced NaN here, and
  // NaN.toFixed(1) rendered the literal string "NaN%" on the card.
  if (!Number.isFinite(value)) return null;

  // Derive direction from the ROUNDED value, not the raw one: -0.03 rounds to
  // -0, which prints as "0" — previously that rendered a red down-arrow reading
  // "0% — worse than prior period".
  const rounded = Math.round(value * 10) / 10;
  const direction = rounded > 0 ? 'up' : rounded < 0 ? 'down' : 'flat';
  const isGood = direction === 'flat' || goodDirection === 'neutral' ? null : direction === goodDirection;
  const color = isGood === null ? COLORS.muted : isGood ? COLORS.green : COLORS.red;
  const Icon =
    direction === 'up' ? TrendingUpRounded : direction === 'down' ? TrendingDownRounded : TrendingFlatRounded;

  const percentText = Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
  // Colour alone can't carry better/worse for a colourblind reader.
  const meaning =
    isGood === null ? 'versus prior period' : isGood ? 'better than prior period' : 'worse than prior period';

  return (
    <Box
      sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, color }}
      aria-label={`${direction === 'up' ? '+' : ''}${percentText}% — ${meaning}`}
    >
      <Icon sx={{ fontSize: 16 }} />
      <Typography variant='caption' sx={{ fontWeight: 600, color }}>
        {direction === 'up' ? '+' : ''}
        {percentText}%{suffix ? ` ${suffix}` : ''}
      </Typography>
    </Box>
  );
};

export default Delta;
